import React from 'react';
import { useTranslation } from 'react-i18next';
import { SocialIcon, type SocialPlatform, normalizePlatformName } from '../icons/SocialIcon';

interface PersonaItem {
  id: string;
  avatarURL?: string;
  firstName?: string;
  lastName?: string;
  country?: string;
  quote?: string;
  socialNetworks?: Array<{ platform: string; pertinence?: string; frequency?: string }>;
  brandId?: string;
  brandName?: string;
  jobTitle?: string;
  languages?: string[];
}

interface PersonasCardProps {
  personas: PersonaItem[];
}

export const PersonasCard: React.FC<PersonasCardProps> = ({ personas }) => {
  const { t } = useTranslation();
  if (!Array.isArray(personas) || personas.length === 0) return null;

  const getFlagForLanguage = (code?: string): string => {
    if (!code) return '🌐';
    const c = code.toLowerCase();
    switch (c) {
      case 'en': case 'en-gb': return '🇬🇧';
      case 'en-us': return '🇺🇸';
      case 'fr': return '🇫🇷';
      case 'de': return '🇩🇪';
      case 'es': return '🇪🇸';
      case 'it': return '🇮🇹';
      case 'pt': return '🇵🇹';
      case 'pt-br': return '🇧🇷';
      case 'nl': return '🇳🇱';
      case 'pl': return '🇵🇱';
      case 'ru': return '🇷🇺';
      case 'zh': case 'zh-cn': return '🇨🇳';
      case 'zh-tw': return '🇹🇼';
      case 'ja': return '🇯🇵';
      case 'ko': return '🇰🇷';
      case 'ar': return '🇸🇦';
      case 'tr': return '🇹🇷';
      case 'hi': return '🇮🇳';
      default: return '🌐';
    }
  };

  const capitalizeFirst = (value?: string): string => {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  return (
    <section className="w-full bg-white rounded-xl border border-black shadow-lg p-3 sm:p-4">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.personas.title', 'Personas')}</h3>
        <span className="bg-indigo-100 text-indigo-700 text-xs font-medium px-2.5 py-0.5 rounded-full">{personas.length}</span>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {personas.map((p) => {
          const name = [p.firstName, p.lastName].filter(Boolean).join(' ') || t('dashboard.personas.unknown', 'Unknown');
          const networks = Array.isArray(p.socialNetworks) ? p.socialNetworks : [];
          return (
            <div key={p.id} className="flex gap-3 p-3 border border-black rounded-lg bg-white">
              <div className="w-16 h-16 rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
                {p.avatarURL ? (
                  <img src={p.avatarURL} alt={name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">👤</div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                    {Array.isArray(p.languages) && p.languages.length > 0 && (
                      <span className="ml-1 text-base leading-none flex-shrink-0">
                        {p.languages.slice(0, 4).map((lc, i) => (
                          <span key={`${p.id}-lang-${i}`} className="ml-0.5">{getFlagForLanguage(lc)}</span>
                        ))}
                      </span>
                    )}
                  </div>
                  {p.country && <span className="text-[11px] text-gray-500 ml-2 truncate">{p.country}</span>}
                </div>
                {p.jobTitle && (
                  <p className="text-[11px] italic text-gray-600 truncate">{p.jobTitle}</p>
                )}
                {p.quote && (
                  <p className="mt-1 text-xs text-gray-600 line-clamp-2">{p.quote}</p>
                )}
                {networks.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {networks.map((n, idx) => (
                      <span key={`${p.id}-${n.platform}-${idx}`} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-white border border-gray-300 text-gray-700">
                        <SocialIcon platform={normalizePlatformName(n.platform) as SocialPlatform} size={12} />
                        <span className="hidden sm:inline">{capitalizeFirst(n.frequency)}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PersonasCard;


